import { useEffect, useMemo } from 'react'

/**
 * Reusable unsaved-changes guard for admin forms.
 *
 * Compares a serialised snapshot of `current` against `original` to
 * decide whether the form is dirty, then layers four protection
 * mechanisms - all gated on ``isDirty && !suppressed``:
 *
 *  - **`beforeunload`** for tab close, refresh, browser-leaves-document.
 *  - **Capture-phase `document.click`** for SPA `<Link>` clicks
 *    (sidebar nav, breadcrumbs, etc). Fires before React Router's
 *    handler - prompting on cancel preventDefaults so the navigation
 *    never happens.
 *  - **`popstate`** for the browser back / forward buttons. Restores
 *    the recorded URL on cancel.
 *  - **`confirmDiscard()`** for in-form Cancel buttons that call
 *    `navigate()` programmatically.
 *
 * **Why not `useBlocker`?** It only works under a data router
 * (``createBrowserRouter`` + ``RouterProvider``). This app uses
 * ``<BrowserRouter>``, so we re-implement the same guarantee with
 * document-level event listeners.
 *
 * **Modifier-click is allowed through unprompted by design** - cmd/ctrl
 * +click opens in a new tab and doesn't disturb the form, so prompting
 * would be noise.
 *
 * **Contract:** ``current`` and ``original`` must be JSON-serialisable.
 * The dirty check uses ``JSON.stringify`` so unserialisable values
 * (``Map``/``Set``, class instances, functions) compare as equal blobs
 * and the guard will silently miss edits. Plain objects, primitives,
 * arrays, and ``Date`` (which serialises to a stable ISO string) are
 * all fine.
 *
 * Usage:
 * ```ts
 * const { isDirty, confirmDiscard } = useUnsavedChangesGuard(
 *   formData,
 *   originalFormData,
 *   { suppressed: saving },
 * )
 *
 * const handleCancel = () => {
 *   if (!confirmDiscard()) return
 *   navigate(returnUrl)
 * }
 * ```
 *
 * `suppressed` lets the form turn the guard off briefly while it's
 * actively submitting, so the post-save programmatic navigate isn't
 * intercepted by our own click listener.
 */
export interface UnsavedChangesGuard {
  isDirty: boolean
  /**
   * Returns true if it's safe to proceed with navigation (form is clean
   * or the user accepted the discard prompt). Returns false if the user
   * cancelled - caller should abort the navigation.
   */
  confirmDiscard: (message?: string) => boolean
}

export interface UnsavedChangesGuardOptions {
  /** Disable the guard temporarily (e.g. while a save is in flight). */
  suppressed?: boolean
  /** Custom prompt message. Defaults to a generic discard prompt. */
  message?: string
}

const DEFAULT_MESSAGE = 'You have unsaved changes. Discard them and leave?'

export function useUnsavedChangesGuard<T>(
  current: T,
  original: T,
  options: UnsavedChangesGuardOptions = {},
): UnsavedChangesGuard {
  // JSON.stringify is sufficient for typical admin forms: every field is
  // a primitive, a Date (Date->ISO string is reflexive), or an array of
  // plain objects. Edit-then-revert collapses back to clean naturally.
  const isDirty = useMemo(
    () => JSON.stringify(current) !== JSON.stringify(original),
    [current, original],
  )

  const suppressed = !!options.suppressed
  const message = options.message ?? DEFAULT_MESSAGE
  const active = isDirty && !suppressed

  // 1. Browser tab close / refresh / browser-leaves-document.
  useEffect(() => {
    if (!active) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [active])

  // 2. SPA <Link> clicks. Capture phase so we run BEFORE React Router's
  // synthetic onClick handler.
  useEffect(() => {
    if (!active) return
    const handleClick = (e: MouseEvent) => {
      // Modifier-clicks open in new tab; left-click only.
      if (e.button !== 0) return
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (!target) return
      const link = target.closest('a')
      if (!link) return
      if (link.target === '_blank') return
      if (link.hasAttribute('download')) return
      const href = link.getAttribute('href')
      if (!href) return
      // Hash-only nav stays on the same page - don't prompt.
      if (href.startsWith('#')) return
      // External absolute link (different origin) - browser will leave
      // the document, beforeunload will catch it.
      if (/^[a-z]+:\/\//i.test(href)) {
        try {
          const url = new URL(href, window.location.href)
          if (url.origin !== window.location.origin) return
        } catch {
          return
        }
      }
      // intentionally native: the decision must be synchronous so we can
      // preventDefault the in-flight click - an async ConfirmDialog can't.
      if (!window.confirm(message)) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [active, message])

  // 3. Browser back / forward. We push a sentinel history entry on top
  // of the form's URL when the form goes dirty. The first browser-back
  // pops the sentinel, landing on the SAME URL (the form's URL), so
  // React Router doesn't see a route change and doesn't unmount the
  // form before we get a chance to prompt. On cancel we go forward to
  // restore the sentinel; on confirm we go back again to actually leave.
  useEffect(() => {
    if (!active) return
    window.history.pushState({ __unsavedGuard: true }, '', window.location.href)
    let suppressNext = false
    const handlePopState = () => {
      if (suppressNext) {
        suppressNext = false
        return
      }
      // intentionally native: popstate fires synchronously; we must decide
      // before the browser settles the history move.
      if (window.confirm(message)) {
        // User confirmed - go past the form's underlying entry to
        // the route they were originally trying to reach.
        suppressNext = true
        window.history.back()
        return
      }
      // User cancelled - restore the sentinel so the trap stays armed.
      suppressNext = true
      window.history.forward()
    }
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
      // If we still own the sentinel (form is being torn down without
      // a proper navigate, e.g. user untyped back to clean), pop it so
      // the user doesn't have an extra back press to navigate fully.
      if ((window.history.state as { __unsavedGuard?: boolean } | null)?.__unsavedGuard) {
        window.history.back()
      }
    }
  }, [active, message])

  // 4. Programmatic Cancel-button path.
  const confirmDiscard = (msg = message): boolean => {
    if (!active) return true
    // intentionally native: callers expect a synchronous boolean to gate a
    // following navigate() - an async ConfirmDialog can't satisfy that contract.
    return window.confirm(msg)
  }

  return { isDirty, confirmDiscard }
}
